import { Alert, AlertDescription, AlertTitle, CardContent } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { IEtagged } from '@/domains/api';
import { oversightApi } from '../../api/oversight-api';
import type {
  IOversightResponseDetail,
  IOversightResponseRow
} from '../../api/oversight-contracts';
import { LoadError, Section, useAdminData } from '../../components/admin-page';
import { ResponseAnswers } from '../response-answers-page';

/**
 * A survey tab's Answers (SR-431, and SR-427's first half): the questionnaire of the record's
 * current response of that survey — the strip's — drawn by the response view's own Answers tab
 * (`ResponseAnswers`), over the same oversight read. Before it, a survey tab drew no answer at all,
 * only the staff fields, so a value a list import or a grid edit corrected read as new on the
 * response and as never written on the record page. Now both screens read one written value, in
 * the survey's question order, and a correction made here is the same write the response's tab
 * makes.
 *
 * Its own read below a loaded hub, with the three arms; never re-read on the poll or on `written`
 * — no other write on the page moves an answer. A correction landing adopts its answer, as on the
 * response, then re-reads the hub, whose strip says the response's progress and last change.
 * Remounted per response, the response view's lesson: what the tab keeps per address must not
 * follow another response.
 */
export function SurveyAnswersSection({
  current,
  others,
  reread,
  anchor
}: {
  current: IOversightResponseRow;
  /** How many other responses of the survey the record holds — listed under Responses. */
  others: number;
  reread: () => Promise<boolean>;
  anchor: string;
}) {
  return (
    <Section
      title="Answers"
      anchor={anchor}
      description={
        'The answers of the current response, in the survey’s order — the same answers as the ' +
        'response’s own Answers tab, with every correction, list edit and import as written.'
      }
    >
      <CardContent className="grid gap-4 text-sm">
        {/* The strip above says the response — its cycle, status and link; only the others are
            said here, since the answers are the current one's alone. */}
        {others > 0 && (
          <p className="text-muted-foreground">
            The current response’s answers — {others} other{' '}
            {others === 1 ? 'response is' : 'responses are'} under Responses.
          </p>
        )}
        <CurrentAnswers key={current.responseId} responseId={current.responseId} reread={reread} />
      </CardContent>
    </Section>
  );
}

function CurrentAnswers({
  responseId,
  reread
}: {
  responseId: string;
  reread: () => Promise<boolean>;
}) {
  const { data, error, reload, adopt } = useAdminData<IEtagged<IOversightResponseDetail>>(
    (signal) => oversightApi.getResponse(responseId, signal),
    [responseId]
  );
  const adoptAndReread = React.useCallback(
    (next: IEtagged<IOversightResponseDetail>) => {
      adopt(next);
      void reread();
    },
    [adopt, reread]
  );

  if (data === null) {
    return error ? (
      <LoadError message={error} />
    ) : (
      <p className="text-muted-foreground">Reading the answers…</p>
    );
  }
  return (
    <>
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not refresh — showing the answers as last read</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <ResponseAnswers
        response={data.body}
        etag={data.etag}
        reload={reload}
        adopt={adoptAndReread}
        showUnansweredFirst
        level={3}
        anchorPrefix="survey-answers"
      />
    </>
  );
}
